import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  PlanVersionAuthor,
  PlanKind,
  PlanVersionMetadata,
  PlanVersionSummary,
} from 'app/frontend/base/api/plan_versions'

dayjs.extend(RelativeTime)

type Props = {
  kind: PlanKind
  open: boolean
  onOpenChange: (open: boolean) => void
  onView: (versionId: number) => void
  versions: PlanVersionSummary[]
  pendingAuthors?: PlanVersionAuthor[]
  isLoading: boolean
  currentCounts: PlanVersionMetadata
}

const formatCounts = (
  kind: PlanKind,
  metadata: PlanVersionMetadata,
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  if (kind === 'raid') {
    return t('dashboard.team.plans.versions.raid-counts', {
      actors: t('dashboard.team.plans.versions.actor-count', {
        count: metadata.actorCount ?? 0,
      }),
      casts: t('dashboard.team.plans.versions.cast-count', {
        count: metadata.castCount ?? 0,
      }),
      assignments: t('dashboard.team.plans.versions.assignment-count', {
        count: metadata.assignmentCount ?? 0,
      }),
    })
  }
  return t('dashboard.team.plans.versions.visual-counts', {
    objects: t('dashboard.team.plans.versions.object-count', {
      count: metadata.objectCount ?? 0,
    }),
    screens: t('dashboard.team.plans.versions.screen-count', {
      count: metadata.screenCount ?? 0,
    }),
  })
}

const VISIBLE_AUTHORS = 2

const AuthorsCell = ({
  authors,
  t,
}: {
  authors: PlanVersionSummary['authors']
  t: (key: string, options?: Record<string, unknown>) => string
}) => {
  const labels = authors
    .map((author) => author.label)
    .filter((label): label is string => Boolean(label))
  if (labels.length === 0) {
    return (
      <span className='text-muted-foreground'>
        {t('dashboard.team.plans.versions.no-authors')}
      </span>
    )
  }

  const visible = labels.slice(0, VISIBLE_AUTHORS)
  const extra = labels.length - visible.length
  const summary =
    extra > 0
      ? `${visible.join(', ')} ${t(
          'dashboard.team.plans.versions.authors-overflow',
          { count: extra },
        )}`
      : visible.join(', ')

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className='block max-w-44 truncate'>{summary}</span>
      </TooltipTrigger>
      <TooltipContent className='max-w-xs'>{labels.join(', ')}</TooltipContent>
    </Tooltip>
  )
}

const PlanVersionHistoryDialog = ({
  kind,
  open,
  onOpenChange,
  onView,
  versions,
  pendingAuthors = [],
  isLoading,
  currentCounts,
}: Props) => {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open ? (
        <DialogContent className='max-w-3xl'>
          <DialogHeader>
            <DialogTitle>
              {t('dashboard.team.plans.versions.title')}
            </DialogTitle>
            <DialogDescription>
              {t('dashboard.team.plans.versions.description')}
            </DialogDescription>
          </DialogHeader>
          <div className='max-h-[65vh] overflow-y-auto'>
            {isLoading && versions.length === 0 ? (
              <div className='h-32 flex items-center justify-center text-muted-foreground'>
                <Loader2 className='h-6 w-6 animate-spin' />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className='w-12'>
                      {t('dashboard.team.plans.versions.number')}
                    </TableHead>
                    <TableHead>
                      {t('dashboard.team.plans.versions.when')}
                    </TableHead>
                    <TableHead>
                      {t('dashboard.team.plans.versions.authors')}
                    </TableHead>
                    <TableHead>
                      {t('dashboard.team.plans.versions.contents')}
                    </TableHead>
                    <TableHead className='text-right'>
                      {t('dashboard.team.plans.versions.action')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className='tabular-nums text-muted-foreground'>
                      {versions.length + 1}
                    </TableCell>
                    <TableCell className='whitespace-nowrap'>
                      <Badge variant='outline'>
                        {t('dashboard.team.plans.versions.current')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <AuthorsCell authors={pendingAuthors} t={t} />
                    </TableCell>
                    <TableCell>
                      {formatCounts(kind, currentCounts, t)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                  {versions.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className='text-sm text-muted-foreground text-center'
                      >
                        {t('dashboard.team.plans.versions.empty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    versions.map((version, index) => (
                      <TableRow key={version.id}>
                        <TableCell className='tabular-nums text-muted-foreground'>
                          {versions.length - index}
                        </TableCell>
                        <TableCell className='whitespace-nowrap'>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>{dayjs(version.createdAt).fromNow()}</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {dayjs(version.createdAt).format(
                                'YYYY-MM-DD HH:mm',
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <AuthorsCell authors={version.authors} t={t} />
                        </TableCell>
                        <TableCell>
                          {formatCounts(kind, version.metadata, t)}
                        </TableCell>
                        <TableCell className='text-right'>
                          <Button
                            variant='outline'
                            size='xs'
                            className='ml-auto'
                            onClick={() => onView(version.id)}
                          >
                            {t('shared.view')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      ) : null}
    </Dialog>
  )
}

export default PlanVersionHistoryDialog
